import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Download, QrCode as QrCodeIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Booking QR code panel for the Innstillinger page.
 *
 * Renders a 192x192 preview canvas + a "Last ned QR-kode" button that
 * exports a high-resolution (1024px) PNG named after the business slug.
 *
 * Pure client-side via the `qrcode` library — no backend round trips and
 * no extra Mongo writes. The QR encodes the same public booking URL that
 * the "Kopier bookinglenke" button copies, so the two stay in sync
 * automatically (even while a Super Admin is impersonating, because
 * Settings already resolves `biz.slug` from the impersonated business).
 */
export default function BookingQrCode({ bookingUrl, slug, businessName }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!bookingUrl || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, bookingUrl, {
      width: 176,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#1E3B33", light: "#ffffff" },
    })
      .then(() => { if (!cancelled) setReady(true); })
      .catch(() => { if (!cancelled) setReady(false); });
    return () => { cancelled = true; };
  }, [bookingUrl]);

  const download = async () => {
    try {
      const dataUrl = await QRCode.toDataURL(bookingUrl, {
        width: 1024,
        margin: 2,
        errorCorrectionLevel: "M",
        color: { dark: "#1E3B33", light: "#ffffff" },
      });
      const safeSlug = (slug || "bedrift").replace(/[^a-z0-9-]/gi, "-");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${safeSlug}-booking-qr.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("QR-kode lastet ned");
    } catch {
      toast.error("Kunne ikke laste ned QR-kode");
    }
  };

  return (
    <div
      className="rounded-xl border border-border bg-muted/40 p-4 mt-3 flex flex-col items-center gap-3 min-w-0 overflow-hidden"
      data-testid="booking-qr-section"
    >
      <div className="shrink-0 rounded-lg border border-border bg-white p-2">
        <canvas
          ref={canvasRef}
          className="block max-w-full h-auto"
          width={176}
          height={176}
          data-testid="booking-qr-canvas"
          aria-label={`QR-kode for booking til ${businessName || "bedriften"}`}
        />
      </div>
      <div className="w-full min-w-0 text-center">
        <div className="flex items-center gap-2 justify-center">
          <QrCodeIcon className="w-4 h-4 text-primary shrink-0" strokeWidth={1.75} />
          <p className="font-medium text-sm">QR-kode for bookinglenken</p>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Skann med mobilkamera for å åpne bookingsiden. Skriv ut eller del bildet — fungerer offline.
        </p>
        <Button
          variant="outline"
          className="rounded-xl mt-3 w-full whitespace-normal h-auto py-2"
          onClick={download}
          disabled={!ready}
          data-testid="download-qr-btn"
        >
          <Download className="w-4 h-4 mr-1 shrink-0" />
          <span>Last ned QR-kode</span>
        </Button>
      </div>
    </div>
  );
}
